import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  applyActionCode,
  checkActionCode
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../config/firebase.js';
import { identifyUser, resetAnalytics } from '../utils/analytics';
import { trackSignupCompleted, setMetaUser, readMetaCookies } from '../utils/metaPixel';
import { captureSignupLocale } from '../utils/signupLocale';
import { captureSignupDevice, hasMobileUserAgent, inAppBrowserName } from '../utils/device';
import { readReferral, clearReferral } from '../utils/referral';
import { api } from '../services/api.js';

/**
 * Raised instead of starting a Google sign-in that cannot finish, so a caller can offer the
 * way out rather than discovering the dead end by watching a user hit it. Exported so the
 * pages match on this instead of a hand-copied string.
 */
export const IN_APP_BROWSER_ERROR = 'auth/in-app-browser';

/**
 * How long a popup may show no sign of existing before we treat it as swallowed.
 *
 * Long enough that a slow phone opening a real tab is not cut off, short enough that nobody
 * sits looking at a button that did nothing. The visibility and focus checks do the real work;
 * this only decides when to look.
 */
const POPUP_STALL_MS = 4000;

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Encryption service interface - ready for third-party integration
class EncryptionService {
  constructor() {
    this.isInitialized = false;
    this.encryptionKey = null;
  }

  async initialize(userId) {
    console.log('Encryption service initialized for user:', userId);
    this.isInitialized = true;
    return true;
  }

  async encrypt(data) {
    if (!this.isInitialized) {
      throw new Error('Encryption service not initialized');
    }
    return btoa(JSON.stringify(data));
  }

  async decrypt(encryptedData) {
    if (!this.isInitialized) {
      throw new Error('Encryption service not initialized');
    }
    return JSON.parse(atob(encryptedData));
  }
}

const encryptionService = new EncryptionService();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [isHandlingRedirect, setIsHandlingRedirect] = useState(true); // Track redirect handling
  const [redirectError, setRedirectError] = useState(() => {
    // Initialize from localStorage if available
    const storedError = localStorage.getItem('redirectError');
    return storedError || null;
  }); // Store errors from redirect flow

  /**
   * FIX #3: Fetch user profile with retry logic
   * Firestore takes time to create document after signup, so we retry
   */
  const fetchUserProfile = async (uid, maxRetries = 3, delay = 500) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Fetching user profile (attempt ${attempt}/${maxRetries})...`);
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const profileData = userSnap.data();
          setUserProfile(profileData);
          console.log('✅ User profile loaded successfully');
          return profileData;
        } else if (attempt < maxRetries) {
          console.log(`User profile not found, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          console.log('User document does not exist after all retries');
          setUserProfile(null);
          return null;
        }
      } catch (error) {
        console.error(`Error fetching user profile (attempt ${attempt}):`, error);
        if (attempt === maxRetries) {
          setUserProfile(null);
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  /**
   * Creates a user profile document in Firestore
   * Creates document at users/{uid} with required fields
   */
  const createUserProfile = async (user, additionalData = {}) => {
    try {
      if (!user || !user.uid) {
        throw new Error('Invalid user object');
      }

      // Ensure user is authenticated and get auth token
      await user.getIdToken();
      console.log('Auth token obtained, user UID:', user.uid);

      const userRef = doc(db, 'users', user.uid);
      console.log('Checking if user document exists:', user.uid);

      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.log('Creating new user document...');

        // Create user document with required structure
        const userData = {
          uid: user.uid,
          email: user.email || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Browser timezone/language, the only location signal we get without asking.
          // Set here rather than in the signup form so the Google path is covered too —
          // those users never submit a form and would otherwise have no country at all.
          signupLocale: captureSignupLocale(),
          // Which kind of device the account was made on.
          //
          // Nothing recorded this before, which is why "who signed up on a phone and then
          // hit the desktop wall?" could not be answered for any of the first 2,262 accounts
          // — the answer had to be approximated by "signed up and then did nothing". One
          // field, written at the same moment as the locale, makes that segment exact from
          // here on, and it is what tells a mobile signup apart from someone who simply
          // never came back.
          signupDevice: captureSignupDevice(),
          // Where they came from, for the server-side conversion that reports the payment.
          ...metaAttributionFields(),
          // Initialize new users with free plan subscription
          subscription: {
            plan: 'free',
            status: 'active',
            usage: {
              abandonedSearches: 0,
              aiImprovements: 0,
              ideas: 0
            },
            createdAt: serverTimestamp(),
            lastResetDate: new Date().toISOString()
          },
          ...additionalData
        };

        console.log('Creating user document at path:', `users/${user.uid}`);
        console.log('User data:', { uid: user.uid, email: userData.email });

        // Create user document in Firestore
        await setDoc(userRef, userData);
        console.log('User document created successfully with free plan subscription');

        /*
         * The account is real as of the write above. Reported from here rather than from the
         * signup form because this is the only branch both routes to a new account pass
         * through -- the email form calls createUserProfile directly, a first Google sign-in
         * arrives via ensureUserProfile -- and because it cannot fire for someone signing in
         * again, which the form's success path could not distinguish.
         */
        trackSignupCompleted({
          method: user.providerData?.[0]?.providerId === 'password' ? 'email' : 'google',
        });

        // FIX #3: Use fetchUserProfile with retry to ensure document is ready
        const created = await fetchUserProfile(user.uid);

        console.log('User profile creation completed successfully');
        return created;
      } else {
        console.log('User document already exists, updating timestamp...');
        // Update existing user's updatedAt timestamp
        await updateDoc(userRef, {
          updatedAt: serverTimestamp()
        });

        const existingData = userSnap.data();
        setUserProfile(existingData);
        console.log('User profile updated');
        return existingData;
      }
    } catch (error) {
      console.error('❌ Error creating user profile:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('User UID:', user?.uid);
      console.error('Auth current user:', auth.currentUser?.uid);

      // Provide helpful error message for permission errors
      if (error.code === 'permission-denied') {
        const helpfulError = new Error(
          'Firestore permission denied. Please deploy Firestore rules to Firebase Console: ' +
          'https://console.firebase.google.com/project/autoinvent-b4450/firestore/rules'
        );
        helpfulError.code = error.code;
        helpfulError.originalError = error;
        throw helpfulError;
      }

      // Re-throw error so calling code can handle it
      throw error;
    }
  };

  // Errors from signInWithPopup that mean "popup is unavailable, fall back to redirect"
  const POPUP_FALLBACK_ERROR_CODES = new Set([
    'auth/popup-blocked',
    'auth/operation-not-supported-in-this-environment',
    'auth/web-storage-unsupported',
  ]);

  /**
   * Errors that mean "this attempt is over, and that is fine" — return null, don't throw.
   *
   * `auth/cancelled-popup-request` used to sit in the fallback set above, which was a
   * misreading of what it means. It is not the browser refusing a popup. Firebase raises it
   * on the FIRST request when a second one supersedes it — a double-click, a re-render, any
   * duplicate call — and the second request is still running and will succeed. Treating that
   * as "popups don't work here" sent the user down signInWithRedirect instead: a full page
   * teardown, a round trip through Google and the auth handler, and a cold re-download of
   * the entire app bundle, all to recover from a duplicate click that had already been
   * handled. Returning null lets the superseding attempt finish and drive navigation.
   */
  const POPUP_CANCELLED_CODES = new Set([
    'auth/popup-closed-by-user',
    'auth/user-cancelled',
    'auth/cancelled-popup-request',
  ]);

  /**
   * The profile write a brand-new account is in the middle of.
   *
   * onAuthStateChanged fires the instant the credential lands, which on a first sign-up is
   * BEFORE ensureUserProfile has written users/{uid} — the two start together and the write
   * is deliberately not awaited (see signInWithProvider). The listener's own read therefore
   * missed a document that was on its way, slept 500ms, and missed again: every new account
   * paid up to three Firestore reads and 1.5s of timers to discover a document this file was
   * already writing, and ended up with a null profile if the write lost the race outright.
   *
   * Sharing the promise makes the listener WAIT on that write instead of racing it. A
   * returning account has nothing in flight and reads exactly as before.
   */
  const profileWriteInFlight = React.useRef(null);

  /**
   * The Meta attribution cookies, as fields to merge onto the user document.
   *
   * `_fbc` carries the ad click and lives on this domain only, so the Stripe webhook that
   * reports the actual payment — a different process, up to three days later — has no way to
   * read it. Copying it here is what lets services/meta-capi.service.ts tie a subscription
   * charge back to the campaign that produced it.
   *
   * Absent cookies write nothing rather than null. Someone can arrive from an ad, sign up,
   * and pay from a later session with the cookie expired; blanking the field on that visit
   * would throw away the only record of where they came from.
   */
  const metaAttributionFields = () => {
    const { fbc, fbp } = readMetaCookies();
    return { ...(fbc ? { metaFbc: fbc } : {}), ...(fbp ? { metaFbp: fbp } : {}) };
  };

  // Ensure a Firestore user doc exists for the signed-in user, then load profile
  const ensureUserProfile = async (user) => {
    const work = (async () => {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        // Brand-new account via an OAuth provider. Google gives us a displayName and email
        // but never a phone number, and the user never submitted the consent disclosure —
        // so flag the account and collect both in CompleteProfileModal on first load.
        // The flag is only ever written at creation, so existing accounts are untouched.
        return createUserProfile(user, { needsProfileCompletion: true });
      }
      await updateDoc(userDocRef, { updatedAt: serverTimestamp(), ...metaAttributionFields() });
      const existing = userDoc.data();
      setUserProfile(existing);
      return existing;
    })();

    profileWriteInFlight.current = { uid: user.uid, promise: work };
    try {
      return await work;
    } finally {
      // Only clear our own entry: a second sign-in could have replaced it.
      if (profileWriteInFlight.current?.promise === work) profileWriteInFlight.current = null;
    }
  };

  /**
   * Popup-first sign-in with redirect fallback.
   *
   * Why popup-first: signInWithRedirect silently fails for some users (Safari ITP,
   * blocked third-party cookies, sessionStorage cleared between page loads, custom
   * authDomain handler edge cases) — getRedirectResult returns null and the user
   * is left on /login with no error. Firebase's 2024+ guidance is to prefer popup.
   *
   * Returns `{ user, profileReady }` on success, or null if the user cancelled or we
   * fell back to redirect (page will reload). `profileReady` resolves when the Firestore
   * profile document is in place — see below for what callers must hang off it.
   */
  const signInWithProvider = async (provider) => {
    try {
      setLoading(true);

      /*
       * Inside an app's own browser, refuse before anything starts.
       *
       * Neither route works there, and each fails differently. The popup does not return null
       * from `window.open` -- TikTok swallows the navigation and paints "Open this link in
       * your browser" over a raw auth URL, so `auth/popup-blocked` never fires and nothing
       * falls back. Going straight to redirect instead, which shipped first, gets further and
       * ends up worse: Google authenticates, takes a password, and then the browser sits on
       * `/__/auth/handler` forever, blank. Google will not treat an embedded WebView as a
       * trusted session -- it offers none of the accounts already signed in -- and the
       * credential it returns cannot be read on the way back, because the WebView's storage is
       * partitioned away from the leg that wrote it.
       *
       * That is Google's position on embedded browsers rather than something to fix here. So
       * the caller is told, once, in a way it can act on: Signup and Login catch this and
       * offer the handoff out to a real browser, carrying the answers and the referral code.
       */
      const inApp = inAppBrowserName();
      if (inApp) {
        console.warn(`In-app browser (${inApp}). Google sign-in cannot complete here`);
        const err = new Error('Google sign-in cannot complete inside an in-app browser.');
        err.code = IN_APP_BROWSER_ERROR;
        err.inAppBrowser = inApp;
        throw err;
      }

      try {
        /*
         * Race the popup against a stall, because a hijacked popup reports nothing at all.
         *
         * The name list above is a fast path, not the safety net -- it is a guess about strings
         * an app happens to put in its user agent, and a guess that is wrong fails silently and
         * strands somebody on a blank page. TikTok has already proved that twice.
         *
         * The signal that needs no guessing is what a real popup does to THIS page. Opening one
         * takes the foreground: a desktop browser drops focus, a phone opens a tab and hides
         * us. An app that quietly swallows `window.open` and paints its own screen over the top
         * leaves this document visible and focused, and `signInWithPopup` then neither resolves
         * nor rejects -- it simply waits forever for a window that does not exist.
         *
         * So: still in the foreground after the timeout means no popup opened, whoever we are.
         * Both conditions are required, so a slow-but-real popup is not mistaken for a stall.
         *
         * Nothing is cancelled when this fires. The popup promise stays alive, and if it does
         * resolve later, onAuthStateChanged picks the user up and the app moves on by itself.
         */
        const stalled = new Promise((_, reject) => {
          // Phones only. A desktop popup is a separate window that leaves this page visible by
          // design, so the test below cannot tell one from a swallowed one -- and desktop
          // popups work, so there is nothing here worth risking a false alarm over.
          if (!hasMobileUserAgent()) return;

          setTimeout(() => {
            /*
             * Visibility, and deliberately not document.hasFocus().
             *
             * Focus looked like the stronger signal and is the weaker one: a page can be fully
             * on screen and still report no focus, and this fallback exists precisely because
             * silent suppression is the failure being fixed. On a phone the test is simple --
             * a real popup opens a tab and takes the screen, so we go hidden. Still visible
             * means no tab ever opened.
             */
            if (document.visibilityState !== 'visible') return;
            const err = new Error('Google sign-in cannot complete in this browser.');
            err.code = IN_APP_BROWSER_ERROR;
            err.inAppBrowser = inAppBrowserName() || 'unknown';
            reject(err);
          }, POPUP_STALL_MS);
        });

        const popup = signInWithPopup(auth, provider);
        // Whoever loses the race must not surface later as an unhandled rejection.
        popup.catch(() => {});

        const result = await Promise.race([popup, stalled]);
        /**
         * Deliberately not awaited.
         *
         * This used to block, and it put Firestore between the credential arriving and the
         * app appearing: a document read, and for a first-time account a write plus up to
         * three 500ms retries reading it back. The user was already authenticated for all
         * of it — onAuthStateChanged fires the moment the credential lands — so they sat
         * watching a spinner on a page they had every right to have left already.
         *
         * The ordering that await was protecting is real and still holds; it is just
         * enforced by this promise instead of by making the person wait. Callers chain
         * persistOnboardingToUser off `profileReady`, so the document creation carrying
         * needsProfileCompletion still lands first. A setDoc-merge arriving ahead of it
         * would create the document WITHOUT that flag, and CompleteProfileModal would
         * never fire for a brand-new Google account.
         */
        const profileReady = ensureUserProfile(result.user).catch((err) => {
          console.error('Failed to ensure user profile:', err);
        });
        return { user: result.user, profileReady };
      } catch (popupError) {
        if (POPUP_CANCELLED_CODES.has(popupError.code)) {
          return null;
        }
        if (!POPUP_FALLBACK_ERROR_CODES.has(popupError.code)) {
          throw popupError;
        }
        console.warn('Popup unavailable, falling back to redirect:', popupError.code);
        sessionStorage.setItem('authRedirectInFlight', 'google');
        await signInWithRedirect(auth, provider);
        return null;
      }
    } catch (error) {
      console.error('❌ Error signing in with provider:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign up with email and password
   * Sends custom email verification via Postmark and creates Firestore user document
   */
  const signUp = async (email, password, displayName, profile = {}) => {
    try {
      setLoading(true);

      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update the user's display name if provided
      if (displayName) {
        await updateProfile(user, { displayName });
      }

      // Ensure auth state is ready before accessing Firestore
      await user.getIdToken();

      // Create user profile in Firestore (required structure).
      // `profile` carries the signup-form fields (firstName/lastName/phone) plus the
      // communications-consent record captured at submit time.
      await createUserProfile(user, {
        ...(displayName ? { displayName } : {}),
        ...profile,
      });

      // ✅ Send custom verification email via Postmark
      try {
        const authToken = await user.getIdToken();
        const response = await fetch('/api/auth/send-verification-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            email: user.email,
            // Prefer the real first name from the signup form; fall back to the old
            // displayName-or-email-prefix behaviour for accounts created without one.
            firstName: profile.firstName || displayName || email.split('@')[0],
          }),
        });

        const data = await response.json();

        if (!data.success) {
          console.error('Failed to send verification email:', data.error);
        } else {
          console.log('✅ Custom verification email sent to:', email);
        }
      } catch (emailError) {
        console.error('Error sending verification email:', emailError);
        // Don't throw - allow signup to continue even if email fails
      }

      return userCredential;
    } catch (error) {
      console.error('❌ Error signing up:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      // Here rather than in the auth listener: that fires on every anonymous page load too,
      // and resetting there would mint a fresh anonymous id on each visit.
      resetAnalytics();
      setUserProfile(null);
      // FIX #1: Reset encryption service on logout
      encryptionService.isInitialized = false;
      // Clear the filing-reminder dismissal so the banner returns on next login
      // (sessionStorage otherwise survives a same-tab logout/login).
      sessionStorage.removeItem('filingReminderDismissed');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resend verification email to current user using custom Postmark verification
   */
  const resendVerificationEmail = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No user logged in');

      if (currentUser.emailVerified) {
        throw new Error('Email is already verified');
      }

      // Check for pending collaboration token in localStorage
      let pendingCollabToken = null;
      try {
        const pendingCollabData = localStorage.getItem('pendingCollabConfirmation');
        if (pendingCollabData) {
          const data = JSON.parse(pendingCollabData);
          pendingCollabToken = data.token;
          console.log('📝 Found pending collaboration token to include in verification email');
        }
      } catch (e) {
        console.error('Error parsing pending collab token:', e);
      }

      // Send custom verification email via backend
      const authToken = await currentUser.getIdToken();
      const response = await fetch('/api/auth/send-verification-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          email: currentUser.email,
          firstName: currentUser.displayName || currentUser.email.split('@')[0],
          pendingCollabToken, // Include if present
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send verification email');
      }

      console.log('✅ Verification email resent to:', currentUser.email);
      return { success: true, message: 'Verification email sent' };
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  };

  /**
   * Send password reset email via Postmark (public endpoint).
   * Backend always returns success to prevent email enumeration.
   */
  const sendPasswordReset = async (email) => {
    const res = await fetch('/api/auth/send-password-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to send reset email');
    }
    return data;
  };

  /**
   * Submit a new password using a reset token (public endpoint).
   */
  const resetPasswordWithToken = async (token, newPassword) => {
    const res = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    const data = await res.json();
    if (!data.success) {
      const err = new Error(data.error || 'Failed to reset password');
      err.code = data.code;
      throw err;
    }
    return data;
  };

  /**
   * Reload current user to check if email is verified
   */
  const reloadUser = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No user logged in');
      await currentUser.reload();
      setUser({ ...auth.currentUser }); // Trigger re-render with updated user
      return auth.currentUser?.emailVerified || false;
    } catch (error) {
      console.error('Error reloading user:', error);
      throw error;
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, updates);
      setUserProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const storeEncryptedData = async (dataType, data) => {
    try {
      if (!user) throw new Error('No user logged in');

      const encryptedData = await encryptionService.encrypt(data);
      const userRef = doc(db, 'users', user.uid);

      await updateDoc(userRef, {
        [`encryptedData.${dataType}`]: encryptedData
      });

      return encryptedData;
    } catch (error) {
      console.error('Error storing encrypted data:', error);
      throw error;
    }
  };

  const getEncryptedData = async (dataType) => {
    try {
      if (!user) throw new Error('No user logged in');

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const encryptedData = userData.encryptedData?.[dataType];

        if (encryptedData) {
          return await encryptionService.decrypt(encryptedData);
        }
      }

      return null;
    } catch (error) {
      console.error('Error retrieving encrypted data:', error);
      throw error;
    }
  };

  // Handle redirect result on mount (only used as fallback when popup is blocked)
  useEffect(() => {
    /**
     * Attach a creator's referral code after a REDIRECT sign-in.
     *
     * The popup path attaches it in Signup.jsx, off the profileReady chain. The redirect path
     * never reaches that code: signInWithProvider returns null once it falls back, the browser
     * leaves for Google, and it comes back HERE -- never to the signup form. The code itself
     * survived the round trip in localStorage, which is the whole reason it is stored there.
     * Nothing was reading it back, so the referral was simply dropped.
     *
     * The gap fell on exactly the traffic most likely to be carrying a code. Popups are
     * blocked in in-app browsers, which is where a link tapped inside TikTok or Instagram
     * opens, so the broken path is the likely one for a creator's audience.
     *
     * Silent and non-blocking on failure: sign-in has already succeeded by the time this
     * runs, and a missed commission must not surface as an error on someone's first screen.
     * The server attributes once per account and refuses the rest, so a retry on a later
     * sign-in cannot double-credit.
     */
    const attachStoredReferral = async () => {
      const code = readReferral();
      if (!code) return;
      try {
        await api.attributeReferral(code);
        clearReferral();
        console.log('[referral] attached after redirect sign-in');
      } catch (err) {
        console.error('[referral] could not attach after redirect sign-in:', err);
      }
    };

    const handleRedirectResult = async () => {
      // Read + clear the in-flight flag immediately so reloads don't keep firing it
      const redirectInFlight = sessionStorage.getItem('authRedirectInFlight');
      sessionStorage.removeItem('authRedirectInFlight');

      try {
        const result = await getRedirectResult(auth);

        if (result) {
          await result.user.getIdToken();
          await ensureUserProfile(result.user);
          // After the profile exists: attributeSignup refuses an account it cannot find.
          await attachStoredReferral();
        } else if (redirectInFlight) {
          // We expected a redirect result but got none — silent failure.
          // Common causes: third-party cookies blocked, sessionStorage cleared
          // between page loads (incognito, storage partitioning), Safari ITP.
          const message =
            "Sign-in didn't complete. This can happen if third-party cookies or site data are blocked. Please try again.";
          console.warn('Expected redirect result but got null');
          localStorage.setItem('redirectError', message);
          setRedirectError(message);
        }
      } catch (error) {
        if (error.code === 'auth/popup-closed-by-user') {
          console.log('User cancelled redirect sign-in');
        } else {
          console.error('Error handling redirect result:', error);
          localStorage.setItem('redirectError', error.message);
          setRedirectError(error.message);
        }
      } finally {
        setIsHandlingRedirect(false);
      }
    };

    handleRedirectResult();
  }, []); // Only on mount

  /**
   * FIX #1 & #2: Fixed auth state listener
   * - Initialize encryption service on every auth state change
   * - Don't set loading to false until AFTER profile is fetched AND redirect is handled
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔄 Auth state changed:', currentUser ? `User: ${currentUser.uid}` : 'No user (logged out)');
      console.log('   isHandlingRedirect:', isHandlingRedirect);

      setUser(currentUser);

      if (currentUser) {
        // Attach this browser's analytics to the account before anything can fail below —
        // knowing who they are without their plan still beats anonymous.
        identifyUser(currentUser, null);
        // Same id Meta's server-side events are keyed on, so a webhook days later resolves to
        // this person rather than to a stranger.
        setMetaUser(currentUser.uid);

        try {
          // FIX #1: Initialize encryption service immediately for logged-in user
          await encryptionService.initialize(currentUser.uid);

          /*
           * FIX #2 & #3: load the profile before stopping loading.
           *
           * One read first, with no retries. That is a hit for every returning account and
           * costs exactly what it used to. A miss means the document is most likely still
           * being written a few lines away (see profileWriteInFlight), so the wait is on
           * that write rather than on a blind 500ms timer that knows nothing about it.
           *
           * The read has to happen before the check, not after: the listener is notified the
           * instant the credential lands, which can be marginally BEFORE signInWithProvider
           * gets to call ensureUserProfile. By the time a real Firestore round trip has come
           * back empty, the write is registered. If it somehow is not — the email/password
           * path creates its document elsewhere — this falls through to the old retry loop.
           */
          let profile = await fetchUserProfile(currentUser.uid, 1);
          if (!profile) {
            const pending = profileWriteInFlight.current;
            profile = pending && pending.uid === currentUser.uid
              ? await pending.promise
              : await fetchUserProfile(currentUser.uid);
          }
          // Re-identify with the plan attached. Cheap: PostHog only sends when something changed.
          if (profile) identifyUser(currentUser, profile);
        } catch (error) {
          console.error('Error in auth state change handler:', error);
          setUserProfile(null);
        }
      } else {
        // User logged out
        setUserProfile(null);
        encryptionService.isInitialized = false;
      }

      // FIX #5: Wait for redirect handler to complete before setting loading = false
      // This prevents a race condition where we might redirect to dashboard before
      // the redirect result is fully processed
      if (!isHandlingRedirect) {
        console.log('✅ Setting loading = false, user:', currentUser ? currentUser.uid : 'null');
        setLoading(false);
      } else {
        console.log('⏳ Waiting for redirect handler to complete...');
      }
    });

    return () => unsubscribe();
  }, [isHandlingRedirect]);

  // Separate effect to set loading = false when redirect handling completes
  useEffect(() => {
    if (!isHandlingRedirect && user !== undefined) {
      console.log('✅ Redirect complete, setting loading = false');
      setLoading(false);
    }
  }, [isHandlingRedirect, user]);

  const clearRedirectError = React.useCallback(() => {
    setRedirectError(null);
    localStorage.removeItem('redirectError');
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    isHandlingRedirect,
    redirectError,
    clearRedirectError,
    signInWithProvider,
    signUp,
    signIn,
    logout,
    resendVerificationEmail,
    sendPasswordReset,
    resetPasswordWithToken,
    reloadUser,
    updateUserProfile,
    storeEncryptedData,
    getEncryptedData,
    encryptionService
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
